/**
 * Кассовая смена — общие для API и воркера константы.
 *
 * ЗАЧЕМ ЗДЕСЬ. Причину автозакрытия пишет воркер
 * (`apps/worker/src/tasks/cash-shift-autoclose.ts`), а читает API: в лайт-режиме
 * кассы (план 137) по ней отличают смену, закрытую роботом в 03:59, от смены,
 * которую кассир закрыл с физическим пересчётом ящика. У автозакрытой смены
 * наличные никто не считал, поэтому «расхождение» показывать нельзя — там
 * `closing_cash` просто скопирован из расчётного значения.
 *
 * Строка сравнивается ПОЛНОСТЬЮ (===), поэтому её нельзя менять в одном месте:
 * разъедутся — API перестанет узнавать автозакрытые смены и покажет кассиру
 * ложное «расхождение 0» вместо честного «наличные не пересчитывались».
 */
export const CASH_SHIFT_AUTO_CLOSE_REASON = 'Автоматическое закрытие в 03:59';

/**
 * Причина закрытия смены при увольнении сотрудника (план 155 Ф2, ревью S1).
 * Как и у робота, наличные никто не пересчитывал: «по итогу» = «ожидается».
 * Признак тот же — зарезервированная причина И инвариант `closing = expected`,
 * `variance = 0`; кассир эту причину написать не может (`CloseShiftCommand`).
 */
export const CASH_SHIFT_DISMISSAL_CLOSE_REASON = 'Закрыта при увольнении сотрудника';

/**
 * Причины закрытия БЕЗ пересчёта ящика — зарезервированы за роботом и
 * увольнением. Тот же список стоит в триггере `cash_shifts_enforce_immutability`
 * (исключение 3, миграция 20260930170000) — менять вместе.
 */
export const CASH_SHIFT_UNCOUNTED_CLOSE_REASONS: readonly string[] = [
  CASH_SHIFT_AUTO_CLOSE_REASON,
  CASH_SHIFT_DISMISSAL_CLOSE_REASON,
];

/**
 * Уведомление (колокольчик) «смену не закрыть автоматически: в ящике по
 * расчёту минус» — робот в 03:59 оставил смену открытой (план 154 Ф3, ревью
 * S2). Пишет воркер `cash-shift-autoclose`, текст — каталог
 * `employee-notification-kinds.ts`.
 */
export const CASH_SHIFT_AUTOCLOSE_NEGATIVE = 'cash_shift.autoclose_negative';

/** Причина — одна из зарезервированных за закрытием без пересчёта. */
export function isUncountedCloseReason(reason: string | null | undefined): boolean {
  return (
    reason !== null && reason !== undefined && CASH_SHIFT_UNCOUNTED_CLOSE_REASONS.includes(reason)
  );
}

/**
 * Смена закрыта без пересчёта наличных — роботом в 03:59 (план 150 Ф1-Б) или
 * при увольнении сотрудника (план 155 Ф2) — ревью безопасности.
 *
 * Одного текста причины мало: до запрета зарезервированной причины кассир мог
 * закрыть смену с недостачей и написать ровно эту строку — и пересчёт снимка
 * счёл бы смену «никто не считал» и стёр недостачу (`closing := expected`).
 * Поэтому признак — причина И инвариант автозакрытия: робот всегда пишет
 * `closing_cash = expected_cash`, `variance = 0`. Иначе — смена человека.
 * Тот же признак проверяет триггер `cash_shifts_enforce_immutability`.
 */
export function isAutoClosedShift(s: {
  varianceReason: string | null;
  varianceKopecks: number | null;
  closingCashKopecks: number | null;
  expectedCashKopecks: number | null;
}): boolean {
  return (
    isUncountedCloseReason(s.varianceReason) &&
    s.varianceKopecks === 0 &&
    s.closingCashKopecks !== null &&
    s.closingCashKopecks === s.expectedCashKopecks
  );
}
